import { isSameDay } from 'date-fns';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

import { deleteDoc, doc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { ChangePaymentMethod } from '~/components/ChangePaymentMethod';
import { useAuth } from '~/hooks/auth';
import { useDeliverymen } from '~/hooks/deliverymens';
import { useOrders } from '~/hooks/orders';
import { useToast } from '~/hooks/toast';
import IOrder from '~/interfaces/IOrder';
import Dashboard from '~/layouts/Dashboard';
import POS from '~/pages/POS';
import { bff } from '~/services/bff';
import { DB } from '~/services/firebase';
import {
  ORDER_STATUS,
  ORDER_TYPES,
  ORDER_TYPES_CONSTS,
  PAYMENT_METHODS,
} from '~/utils/getEnums';
import { handlePrint } from '~/utils/handlePrint';

const { REACT_APP_FILTER_ORDERS, REACT_APP_FIREBASE_PROJECT } = process.env;

const Orders: React.FC = () => {
  const { addToast } = useToast();
  const { user, company } = useAuth();

  const {
    loadOrders,
    loadOrdersWithRangeDate,
    loadMore,
    orders,
    approve,
    cancel,
    delivery,
    finished,
    newOrder,
    openPos,
    setOpenPos,
    updateDeliverymen,
    updatePaymentMethod,
    removeOrder,
    initialDate,
    setInitialDate,
    initialHour,
    setInitialHour,
    finalDate,
    setFinalDate,
    finalHour,
    setFinalHour,
  } = useOrders();
  const { deliverymens, loadDeliverymens } = useDeliverymen();
  // const { addToast } = useToast();
  // const [orders, setOrders] = useState<IOrder[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Pedidos de Hoje');
  const [showFilter, setShowFilter] = useState(false);

  const [newOrders, setNewOrders] = useState<string[]>([]);

  const [updateOrder, setUpdateOrder] = useState<IOrder | null>(null);

  // // filter
  // const [initialDate, setInitialDate] = useState(
  //   format(new Date(), 'yyyy-MM-dd'),
  // );
  // const [initialHour, setInitialHour] = useState('00:00');
  // const [finalDate, setFinalDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  // const [finalHour, setFinalHour] = useState('23:59');

  const [loading, setLoading] = useState(false);

  const handleLoadOrdersWithDateRange = useCallback(async () => {
    setLoading(true);
    await loadOrdersWithRangeDate({
      id: user.uid,
      initialDate,
      initialHour,
      finalDate,
      finalHour,
    });
    setLoading(false);
  }, [
    loadOrdersWithRangeDate,
    user,
    initialDate,
    initialHour,
    finalDate,
    finalHour,
  ]);

  useEffect(() => {
    if (REACT_APP_FILTER_ORDERS) {
      handleLoadOrdersWithDateRange();
    }
  }, []);

  useEffect(() => {
    if (orders === null && !REACT_APP_FILTER_ORDERS) {
      loadOrders('');
    }
  }, [orders]);

  // useEffect(() => {
  //   if (!REACT_APP_FILTER_ORDERS && company.uid) {
  //     loadOrders('');
  //   }
  // }, [company.uid]);

  useEffect(() => {
    loadDeliverymens();
  }, [loadDeliverymens]);

  const filteredOrders = useMemo(() => {
    const filtered = (orders || []).filter(order => {
      let result = true;

      if (REACT_APP_FILTER_ORDERS) {
        if (order.code) {
          result =
            order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) ||
            order.code.toLocaleLowerCase().includes(search.toLocaleLowerCase());
        } else {
          result = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        }
      }

      if (!REACT_APP_FILTER_ORDERS) {
        if (status === 'Pedidos de Hoje') {
          const today = new Date();
          const created_at = new Date(order.created_at.seconds * 1000);

          const isSameDate = isSameDay(today, created_at);

          if (order.code) {
            const orderName = order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            const orderCode = order.code
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            result = (orderName || orderCode) && isSameDate;
          } else {
            const orderName = order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase());

            result = orderName && isSameDate;
          }
        } else if (order.code) {
          result =
            order.name
              .toLocaleLowerCase()
              .includes(search.toLocaleLowerCase()) ||
            order.code.toLocaleLowerCase().includes(search.toLocaleLowerCase());
        } else {
          result = order.name
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase());
        }
      }

      return result;
    });

    return filtered;
  }, [search, orders, status]);

  const handleConfirmRemove = useCallback(
    async (id: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do pedido?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await deleteDoc(doc(DB, 'orders', id));

        await bff.delete(`/orders/${REACT_APP_FIREBASE_PROJECT}`);

        removeOrder(id);

        addToast({
          type: 'success',
          description: 'Pedido foi removida com sucesso.',
        });
      }
    },
    [addToast, removeOrder],
  );

  const handleChangeStatus = useCallback(
    async (uid: string, _status: string) => {
      if (_status === '0') await newOrder(uid);
      if (_status === '1') await approve(uid);
      if (_status === '2') await delivery(uid);
      if (_status === '3') await finished(uid);
      if (_status === '4') await cancel(uid);
      if (_status !== '') {
        addToast({
          type: 'success',
          description: 'O status do pedido foi alterado com sucesso!',
        });
      }
    },
    [approve, cancel, delivery, finished, newOrder, addToast],
  );

  return (
    <Dashboard containerMessage="col-xl-12">
      <div className="col-xl-12">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">Pedidos</h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por código ou cliente"
                          onChange={event => {
                            setSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    {!REACT_APP_FILTER_ORDERS && (
                      <li>
                        <div className="drodown">
                          <Link
                            to="#status"
                            className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                            data-toggle="dropdown"
                          >
                            {status}
                          </Link>
                          <div className="dropdown-menu dropdown-menu-right">
                            <ul className="link-list-opt no-bdr">
                              <li>
                                <Link
                                  to="#all"
                                  onClick={() => {
                                    setStatus('Todos');
                                    setShowFilter(false);
                                  }}
                                >
                                  <span>Todos</span>
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#today"
                                  onClick={() => {
                                    setStatus('Pedidos de Hoje');
                                    setShowFilter(false);
                                  }}
                                >
                                  <span>Pedidos de Hoje</span>
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    )}
                    <li className="nk-block-tools-opt">
                      <Link
                        to="#_"
                        className="toggle btn btn-icon btn-primary d-md-none"
                        onClick={() => setOpenPos(true)}
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to="#_"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                        onClick={() => setOpenPos(true)}
                      >
                        <em className="icon ni ni-plus" />
                        <span>Novo Pedido</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {REACT_APP_FILTER_ORDERS && (
          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <form onSubmit={() => {}}>
                <div className="row">
                  <div className="col-sm-3">
                    <div className="form-group mb-3">
                      <label className="form-label">Data Inicial</label>
                      <div className="form-control-wrap">
                        <input
                          id="date"
                          type="date"
                          step=".01"
                          className="form-control"
                          defaultValue={initialDate}
                          onChange={event => {
                            setInitialDate(event.target.value);
                          }}
                        />
                        {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-2">
                    <div className="form-group mb-3">
                      <label className="form-label">Hora Inicial</label>
                      <div className="form-control-wrap">
                        <input
                          id="date"
                          type="time"
                          className="form-control"
                          defaultValue={initialHour}
                          onChange={event => {
                            setInitialHour(event.target.value);
                          }}
                        />
                        {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-3">
                    <div className="form-group mb-3">
                      <label className="form-label">Data Final</label>
                      <div className="form-control-wrap">
                        <input
                          id="date"
                          type="date"
                          step=".01"
                          className="form-control"
                          defaultValue={finalDate}
                          onChange={event => {
                            setFinalDate(event.target.value);
                          }}
                        />
                        {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                      </div>
                    </div>
                  </div>
                  <div className="col-sm-2">
                    <div className="form-group mb-3">
                      <label className="form-label">Hora Final</label>
                      <div className="form-control-wrap">
                        <input
                          id="date"
                          type="time"
                          className="form-control"
                          defaultValue={finalHour}
                          onChange={event => {
                            setFinalHour(event.target.value);
                          }}
                        />
                        {/* {errors.from && (
                            <span className="invalid">{errors.from}</span>
                          )} */}
                      </div>
                    </div>
                  </div>

                  <div
                    className="col-sm-2 d-flex"
                    style={{ alignItems: 'flex-end' }}
                  >
                    <div className="form-group mb-3">
                      <button
                        type="button"
                        onClick={handleLoadOrdersWithDateRange}
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading && (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          />
                        )}
                        {!loading && 'Buscar Pedidos'}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="row tb-card">
          {filteredOrders.length === 0 && (
            <div className="col-md-12">
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="d-flex justify-content-center">
                    Você não possui pedidos.
                  </div>
                </div>
              </div>
            </div>
          )}

          {filteredOrders.map(item => (
            <div className="col-md-6 col-sm-6 mb-3" key={item.uid}>
              <div className="card card-bordered">
                <div className="card-inner">
                  <div className="team">
                    <Link
                      to={`/orders/details/${item.uid}`}
                      style={{ color: '#000', textTransform: 'none' }}
                    >
                      <div className="user-card user-card-s2">
                        {/* <div className="user-avatar lg bg-primary">
                        <span>{getInitialLettersOfName(item.name)}</span>
                        <div className="status dot dot-lg dot-success" />
                      </div> */}
                        <div>
                          <h6 className="mb-1">{item.name}</h6>
                          <span className="sub-text mb-2">{item.whatsapp}</span>

                          {item.client_order_count === 1 && (
                            <span
                              className="badge bg-success"
                              style={{ marginLeft: 4 }}
                            >
                              🎉 PRIMEIRO PEDIDO
                            </span>
                          )}
                          {item.client_order_count && item.client_order_count > 1 && (
                            <span
                              className="badge bg-info"
                              style={{ marginLeft: 4 }}
                            >
                              <em className="icon ni ni-repeat" /> Pedido {item.client_order_count}º
                            </span>
                          )}
                          {item.order_type !== undefined &&
                            !item.table?.uid && (
                              <h6>{ORDER_TYPES[item.order_type]}</h6>
                            )}
                          {item.order_type === '2' && item.table !== null && (
                            <h6>
                              Mesa:
                              {item.table?.name}
                            </h6>
                          )}
                        </div>
                      </div>

                      <ul className="team-info-custom">
                        <li>
                          <span>Pedido</span>
                          <span>{`#${item.code}`}</span>
                        </li>
                        <li>
                          <span>Data e Hora</span>
                          <span className="text-right">
                            {item.created_at_formatted}
                          </span>
                        </li>
                        <li>
                          <span>Total</span>
                          <span>{item.total_formatted}</span>
                        </li>
                        <li>
                          <span>Status</span>
                          {item.status === '0' && (
                            <span className="badge badge-dot badge-primary">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '1' && (
                            <span className="badge badge-dot badge-warning">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '2' && (
                            <span className="badge badge-dot badge-info">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '3' && (
                            <span className="badge badge-dot badge-success">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                          {item.status === '4' && (
                            <span className="badge badge-dot badge-danger">
                              {ORDER_STATUS[item.status]}
                            </span>
                          )}
                        </li>
                      </ul>
                    </Link>
                    <div className="team-view">
                      <div className="form-control-select">
                        <select
                          className="form-control"
                          value={item.status}
                          onChange={event => {
                            if (item.uid) {
                              handleChangeStatus(item.uid, event.target.value);
                            }
                          }}
                          style={{ minWidth: 120 }}
                        >
                          <option value="">Selecione...</option>
                          <option value="0">NOVO PEDIDO</option>
                          <option value="1">PREPARANDO</option>
                          <option value="2">SAIU PARA ENTREGA</option>
                          <option value="3">CONCLUÍDO</option>
                          <option value="4">CANCELADO</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 ps-2 pe-2"
                        onClick={() => handlePrint({ order: item, company })}
                      >
                        <em className="icon ni ni-printer-fill" />
                        IMPRIMIR
                      </button>
                    </div>

                    <div className="team-view flex-column">
                      <div className="form-control-select mb-1">
                        <select
                          className="form-control"
                          value={item.deliverymen}
                          onChange={event => {
                            if (item.uid)
                              updateDeliverymen(item.uid, event.target.value);
                          }}
                        >
                          <option value="">Entregador...</option>
                          {deliverymens &&
                            deliverymens?.map(item => (
                              <option key={item.uid} value={item.uid}>
                                {item.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      {item.orderPayments && item.orderPayments.length > 0 && (
                        <div>
                          <p className="preview-item" style={{ padding: 0 }}>
                            {item.orderPayments.map((obj, index) => (
                              <span
                                key={index}
                                className="badge bg-secondary"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setUpdateOrder(item)}
                              >
                                {PAYMENT_METHODS[obj.payment_method_id]}
                              </span>
                            ))}
                          </p>
                        </div>
                      )}

                      {/* {(item.paymentMethod ||
                        item.orderPayments.length === 1) && (
                        <div className="form-control-select">
                          <select
                            className="form-control"
                            value={
                              item.paymentMethod
                                ? item.paymentMethod
                                : item.orderPayments[0].payment_method_id
                            }
                            onChange={event => {
                              if (item.uid) {
                                updatePaymentMethod(
                                  item.uid,
                                  event.target.value,
                                );
                              }
                            }}
                          >
                            <option value="">Forma de Pagamento...</option>
                            <option value="0">DINHEIRO</option>
                            <option value="1">DÉBITO</option>
                            <option value="2">CRÉDITO</option>
                            <option value="3">PIX</option>
                            <option value="5">IFOOD</option>
                          </select>
                        </div>
                      )} */}

                      <button
                        type="button"
                        onClick={() => handleConfirmRemove(item.uid || '')}
                        className="mt-2"
                      >
                        <em className="icon ni ni-trash-fill me-1" />
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {user && user.uid && (
            <button
              className="page-link"
              type="button"
              onClick={() => loadMore(user.uid)}
              style={{ border: 0 }}
            >
              <em className="icon ni ni-chevrons-right mr-1" />
              Carregar mais
            </button>
          )}
        </div>

        <div className="nk-block tb-status">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <table className="table table-orders">
                  <thead className="tb-odr-head">
                    <tr className="tb-odr-item">
                      <th className="tb-odr-info tb-code">Código</th>
                      <th className="tb-odr-info tb-client">Cliente</th>
                      <th className="tb-odr-info tb-contact">Contato</th>
                      <th className="tb-odr-info tb-total">Total</th>
                      <th className="tb-odr-info tb-status">Status</th>
                      <th className="tb-odr-info tb-status">Entregador</th>
                      <th className="tb-odr-info tb-status">Pagamento</th>
                      <th className="tb-odr-action" />
                    </tr>
                  </thead>
                  <tbody className="tb-odr-body">
                    {filteredOrders.map(order => (
                      <Fragment key={order.uid}>
                        {!order.chatbot && (
                          <tr
                            className="tb-odr-item"
                            key={order.uid}
                            style={{
                              background: order.status === '0' ? '#fceae9' : '',
                            }}
                          >
                            <td>
                              <Link to={`/orders/details/${order.uid}`}>
                                {`#${order.code}`}
                              </Link>
                              {order.fidelitDiscount &&
                              order.fidelitDiscount > 0 ? (
                                <span
                                  className="badge bg-secondary"
                                  style={{ marginLeft: 4 }}
                                >
                                  ⭐ FIDELIDADE
                                </span>
                              ) : (
                                <></>
                              )}

                              <br />
                              {order.created_at_formatted}
                            </td>
                            <td>
                              <strong style={{ textTransform: 'capitalize' }}>
                                {order.name}
                              </strong>
                              {order.client_order_count === 1 && (
                                <span className="badge bg-success ml-2">
                                  🎉 PRIMEIRO PEDIDO
                                </span>
                              )}
                              {order.client_order_count && order.client_order_count > 1 && (
                                <span className="badge bg-info ml-2">
                                  <i className="icon ni ni-repeat" /> Pedido {order.client_order_count}º
                                </span>
                              )}
                              {order.coupon && order.coupon.uid && (
                                <span className="badge bg-secondary ml-2">
                                  {order.coupon.code}
                                </span>
                              )}
                              {order.order_type !== undefined &&
                                !order.table?.uid && (
                                  <span>
                                    <br />
                                    {ORDER_TYPES[order.order_type]}
                                  </span>
                                )}
                              {order.order_type === ORDER_TYPES_CONSTS.TABLE &&
                                order.table !== null && (
                                  <span>
                                    <br />
                                    {`Mesa: ${order.table?.name}`}
                                  </span>
                                )}
                            </td>
                            <td>{order.whatsapp}</td>
                            <td>{order.total_formatted}</td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={order.status}
                                  onChange={event => {
                                    if (order.uid) {
                                      handleChangeStatus(
                                        order.uid,
                                        event.target.value,
                                      );
                                    }
                                  }}
                                  style={{ minWidth: 160 }}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">NOVO PEDIDO</option>
                                  <option value="1">PREPARANDO</option>
                                  <option value="2">SAIU PARA ENTREGA</option>
                                  <option value="3">CONCLUÍDO</option>
                                  <option value="4">CANCELADO</option>
                                </select>
                              </div>
                            </td>

                            <td>
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={order.deliverymen}
                                  onChange={event => {
                                    if (order.uid)
                                      updateDeliverymen(
                                        order.uid,
                                        event.target.value,
                                      );
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  {deliverymens?.map(item => (
                                    <option key={item.uid} value={item.uid}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>

                            <td className="tb-status">
                              {order.orderPayments &&
                              order.orderPayments.length > 0 ? (
                                <p className="preview-item">
                                  {order.orderPayments.map((item, index) => (
                                    <span
                                      className="badge bg-secondary"
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setUpdateOrder(order)}
                                    >
                                      {PAYMENT_METHODS[item.payment_method_id]}
                                    </span>
                                  ))}
                                </p>
                              ) : (
                                <div className="form-control-select">
                                  <select
                                    className="form-control"
                                    value={
                                      order.paymentMethod ??
                                      order.orderPayments[0].payment_method_id
                                    }
                                    onChange={event => {
                                      if (order.uid) {
                                        updatePaymentMethod(
                                          order.uid,
                                          event.target.value,
                                        );
                                      }
                                    }}
                                  >
                                    <option value="">Selecione...</option>
                                    <option value="0">DINHEIRO</option>
                                    <option value="1">DÉBITO</option>
                                    <option value="2">CRÉDITO</option>
                                    <option value="3">PIX</option>
                                    <option value="5">IFOOD</option>
                                  </select>
                                </div>
                              )}
                            </td>

                            <td className="tb-odr-action">
                              {/* <div className="tb-odr-btns d-none d-sm-inline"> */}

                              <button
                                type="button"
                                onClick={() => {
                                  handleConfirmRemove(order.uid || '');
                                }}
                                className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 pe-2"
                              >
                                <em className="icon ni ni-trash-fill me-1" />
                                Excluir
                              </button>
                              <button
                                type="button"
                                className="btn btn-icon btn-white btn-dim btn-sm btn-primary ms-2 pe-2"
                                onClick={() => handlePrint({ order, company })}
                              >
                                <em className="icon ni ni-printer-fill" />
                                IMPRIMIR
                              </button>
                            </td>
                          </tr>
                        )}

                        {order.chatbot && (
                          <tr className="tb-odr-item" key={order.uid}>
                            <td>
                              <Link to={`/orders/details/${order.uid}`}>
                                {`#${order.code}`}
                              </Link>
                              <br />
                              {order.created_at_formatted}
                            </td>
                            <td>
                              <strong style={{ textTransform: 'capitalize' }}>
                                {order.name}
                              </strong>
                              <span>
                                <br />
                                Chatbot Delivery
                              </span>
                              {order.client_order_count === 1 && (
                                <span className="badge bg-success ml-2">
                                  🎉 PRIMEIRO PEDIDO
                                </span>
                              )}
                            </td>
                            <td>{order.whatsapp}</td>
                            <td>
                              {order.total === 0 ? '-' : order.total_formatted}
                            </td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={order.status}
                                  onChange={event => {
                                    if (order.uid) {
                                      handleChangeStatus(
                                        order.uid,
                                        event.target.value,
                                      );
                                    }
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">NOVO PEDIDO</option>
                                  <option value="1">PREPARANDO</option>
                                  <option value="2">SAIU PARA ENTREGA</option>
                                  <option value="3">CONCLUÍDO</option>
                                  <option value="4">CANCELADO</option>
                                </select>
                              </div>
                            </td>

                            <td>
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={order.deliverymen}
                                  onChange={event => {
                                    if (order.uid)
                                      updateDeliverymen(
                                        order.uid,
                                        event.target.value,
                                      );
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  {deliverymens?.map(item => (
                                    <option key={item.uid} value={item.uid}>
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </td>

                            <td className="tb-status">
                              <div className="form-control-select">
                                <select
                                  className="form-control"
                                  value={order.paymentMethod}
                                  onChange={event => {
                                    if (order.uid) {
                                      updatePaymentMethod(
                                        order.uid,
                                        event.target.value,
                                      );
                                    }
                                  }}
                                >
                                  <option value="">Selecione...</option>
                                  <option value="0">DINHEIRO</option>
                                  <option value="1">DÉBITO</option>
                                  <option value="2">CRÉDITO</option>
                                  <option value="3">PIX</option>
                                  <option value="5">IFOOD</option>
                                </select>
                              </div>
                            </td>

                            <td className="tb-odr-action">
                              <button
                                type="button"
                                onClick={() => {
                                  handleConfirmRemove(order.uid || '');
                                }}
                                className="mt-2"
                              >
                                <em className="icon ni ni-trash-fill me-1" />
                                Excluir
                              </button>

                              <button
                                type="button"
                                className="btn btn-icon btn-white btn-dim btn-sm btn-primary pe-2 ms-2"
                                onClick={() => handlePrint({ order, company })}
                              >
                                <em className="icon ni ni-printer-fill" />
                                IMPRIMIR
                              </button>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card-inner">
                <div className="nk-block-between-md g-3">
                  <div className="g">
                    <ul className="pagination justify-content-center justify-content-md-start">
                      <li className="page-item">
                        <button
                          className="page-link"
                          type="button"
                          onClick={() => loadMore(user.uid)}
                        >
                          <em className="icon ni ni-chevrons-right mr-1" />
                          Carregar mais
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setOpenPos(true)}
        style={{
          width: 42,
          height: 42,
          borderRadius: '50%',
          background: '#000',
          color: '#fff',
          position: 'fixed',
          bottom: 10,
          right: 10,
          zIndex: 9,
        }}
      >
        <em className="icon ni ni-plus" />
      </button>

      {updateOrder && (
        <ChangePaymentMethod
          order={updateOrder}
          show={!!updateOrder}
          onClose={() => setUpdateOrder(null)}
        />
      )}

      {openPos && (
        <>
          <POS />
          <button
            onClick={() => setOpenPos(false)}
            type="button"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              zIndex: 9999,
              width: 42,
              height: 42,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <em className="icon fa fa-times" />
          </button>
        </>
      )}
    </Dashboard>
  );
};

export default Orders;
